// client/src/components/MerchantSignup.js

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import '../CSS/signup.css';
import { auth } from '../../firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { getFirestore, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { REGEX as PASSWORD_REGEX, POLICY as PASSWORD_POLICY } from '../../policies/passwordPolicy';
import PasswordField from '../shared/PasswordField';
import LegalAccordion from '../shared/LegalAccordion';
import { BrandMark } from '../shared/BrandLogo';

// Helpers sûrs
const sanitizeEmail = (s) =>
  (s || '')
    .normalize('NFKC')
    .replace(/\s+/g, '')
    .toLowerCase();

// --- Base API robuste ---------------------------------------------------------
// 1) si REACT_APP_API_URL est défini -> on l’utilise
// 2) sinon, si prod -> window.location.origin (ex: https://ecobill.app)
// 3) sinon dev -> http://localhost:5099
const RAW_API =
  (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.trim()) ||
  (process.env.NODE_ENV === 'production'
    ? window.location.origin
    : 'http://localhost:5099');

const API_BASE = RAW_API.replace(/\/+$/, '');

// Sommes-nous sur une Cloud Function directe ? (…cloudfunctions.net/<functionName>)
const isCFDirect = /cloudfunctions\.net\/[^/]+$/i.test(API_BASE);

// Endpoints selon le mode:
// - CF direct: /auth/signup  | /csrf-token
// - Domaine (rewrites): /api/auth/signup | /api/csrf-token
const SIGNUP_ENDPOINT = isCFDirect
  ? `${API_BASE}/auth/signup`
  : `${API_BASE}/api/auth/signup`;

const CSRF_ENDPOINT = isCFDirect
  ? `${API_BASE}/csrf-token`
  : `${API_BASE}/api/csrf-token`;

const normalizePlan = (raw) => {
  if (!raw) return null;
  const v = String(raw).toLowerCase();
  if (v.startsWith('ann')) return 'annual';
  if (v.startsWith('men') || v.startsWith('mon')) return 'monthly';
  if (v === 'annual' || v === 'monthly') return v;
  return null;
};

const MerchantSignup = () => {
  const [email, setEmail]                     = useState('');
  const [password, setPassword]               = useState('');
  const role                                  = 'commercantpending';
  const [message, setMessage]                 = useState({ text: '', isSuccess: false });
  const [isSignupLoading, setIsSignupLoading] = useState(false);
  const [acceptedCGU, setAcceptedCGU]         = useState(false);
  const navigate                              = useNavigate();
  const location                              = useLocation();
  const { code }                              = useParams();
  const planFromState                         = normalizePlan(location.state?.selectedPlan);
  const readStoredPlan = () => {
    try { return normalizePlan(sessionStorage.getItem('merchant_preselected_plan')); } catch { return null; }
  };
  const initialPlan                           = planFromState || readStoredPlan() || null;
  const [selectedPlan, setSelectedPlan]       = useState(initialPlan);
  const territoryFromState                    = (location.state?.selectedTerritory || '').trim();
  const readStoredTerritory = () => {
    try { return sessionStorage.getItem('merchant_preselected_territory') || ''; } catch { return ''; }
  };
  const initialTerritory                      = territoryFromState || readStoredTerritory() || '';
  // eslint-disable-next-line
  const [selectedTerritory, setSelectedTerritory] = useState(initialTerritory);
  const promoFromState                        = (location.state?.promoCode || '').trim();
  const storedPromo                           = (() => { try { return sessionStorage.getItem('merchant_preselected_promo') || ''; } catch { return ''; } })();
  const effectivePromo                        = promoFromState || storedPromo || (code || '');

  // Capture le code dans l'URL puis nettoie
  useEffect(() => {
    if (code) {
      try { sessionStorage.setItem('merchant_preselected_promo', code); } catch {}
      navigate('/merchantsignup', { replace: true, state: { promoCode: code, selectedPlan, selectedTerritory } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, navigate]);

  const isPwdValid = PASSWORD_REGEX.test(password || '');

  // AbortController pour empêcher doublons / requêtes pendantes
  const abortRef = useRef(null);

  const getCookie = (name) => {
    const m = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/([$?*|{}\]\\^])/g, '\\$1') + '=([^;]*)')
    );
    return m ? decodeURIComponent(m[1]) : null;
  };

  useEffect(() => {
    if (initialPlan && initialPlan !== selectedPlan) {
      setSelectedPlan(initialPlan);
    }
  }, [initialPlan, selectedPlan]);

  useEffect(() => {
    if (selectedPlan) {
      try { sessionStorage.setItem('merchant_preselected_plan', selectedPlan); } catch {}
    }
  }, [selectedPlan]);

  useEffect(() => {
    if (selectedTerritory) {
      try { sessionStorage.setItem('merchant_preselected_territory', selectedTerritory); } catch {}
    }
  }, [selectedTerritory]);

  useEffect(() => {
    if (effectivePromo) {
      try { sessionStorage.setItem('merchant_preselected_promo', effectivePromo); } catch {}
    }
  }, [effectivePromo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSignupLoading) return;

    setIsSignupLoading(true);
    setMessage({ text: '', isSuccess: false });

    if (!isPwdValid) {
      setMessage({ text: PASSWORD_POLICY.message, isSuccess: false });
      setIsSignupLoading(false);
      return;
    }

    const safeEmail   = sanitizeEmail(email);
    const rawPassword = password;

    try {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();


      // 0) CSRF (non bloquant si non utilisé côté serveur)
      try {
        await fetch(CSRF_ENDPOINT, { credentials: 'include' });
      } catch {}

      // 1) Appel inscription
      const xsrf = getCookie('XSRF-TOKEN');
      const response = await fetch(SIGNUP_ENDPOINT, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          // IMPORTANT: on utilise X-CSRF-Token (aligné CORS backend)
          ...(xsrf ? { 'X-CSRF-Token': xsrf } : {})
        },
        body: JSON.stringify({
          email:    safeEmail,
          password: rawPassword,
          role:     role
        }),
        signal: abortRef.current.signal,
      });

      let data = {};
      try { data = await response.json(); } catch {}

      if (!response.ok) {
        throw new Error(
          data?.error ||
          (data?.errors && data.errors.map((err) => err.msg).join(', ')) ||
          `Erreur (${response.status}) lors de l’inscription`
        );
      }

      setMessage({ text: 'Inscription réussie ! ✅', isSuccess: true });

      // 2) Login immédiat + Firestore
      // Utiliser l'email retourné par le backend (normalizeEmail backend peut différer du sanitize frontend)
      const loginEmail = data.email || safeEmail;
      const { user } = await signInWithEmailAndPassword(auth, loginEmail, rawPassword);
      await user.getIdToken(true); // force refresh pour que Firestore reçoive les custom claims (role)
      const db = getFirestore();
      await setDoc(
        doc(db, 'users', user.uid),
        { uid: user.uid, role: role, CGU: 'accepted', CGUacceptedAt: serverTimestamp() },
        { merge: true }
      );

      navigate(`/merchantprocesspaiement`, {
        state: { code: effectivePromo, uid: user.uid, selectedPlan, selectedTerritory }
      });

    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Erreur signup merchant :', err);
      setMessage({
        text: err?.message?.startsWith?.('Failed') ? 'API non joignable' : (err?.message || 'Erreur réseau'),
        isSuccess: false
      });
      setTimeout(() => setMessage({ text: '', isSuccess: false }), 4000);
    } finally {
      setIsSignupLoading(false);
    }
  };

  return (
    <div className="eb-auth">
    <div className="signup-container" style={{ margin: 0 }}>
      <div className="eb-auth__brand"><BrandMark /></div>
      <h2 style={{ marginBottom: 6 }}>Inscription commerçant</h2>
      <p className="eb-auth__subtitle" style={{ marginBottom: 22 }}>Créez votre espace Ecobill en quelques secondes</p>
      {selectedPlan && (
        <div
          style={{
            background: 'var(--eb-brand-50)',
            border: '1px solid var(--eb-brand-100)',
            borderRadius: 'var(--eb-radius)',
            padding: '12px 14px',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap'
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--eb-brand-800)' }}>
            Offre sélectionnée : {selectedPlan === 'annual' ? 'Annuel (1 mois offert)' : 'Mensuel'}
          </div>
          <button
            type="button"
            onClick={() => navigate(code ? `/merchantofferselection/${encodeURIComponent(code)}` : '/merchantofferselection')}
            className="eb-link"
            style={{ fontSize: 13.5 }}
          >
            Changer de formule
          </button>
        </div>
      )}
      <form onSubmit={handleSubmit} noValidate>
        <div>
          <label htmlFor="merchant-signup-email">Adresse e-mail</label>
          <input
            id="merchant-signup-email"
            type="email"
            placeholder="vous@entreprise.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isSignupLoading}
            autoComplete="email"
            inputMode="email"
            spellCheck={false}
          />
        </div>

        <div>
          <label>Mot de passe</label>
          <PasswordField
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isSignupLoading}
            required
            autoComplete="new-password"
            pattern={PASSWORD_REGEX.source}
            title={PASSWORD_POLICY.message}
            aria-invalid={!!password && !isPwdValid}
            spellCheck={false}
          />
        </div>

        <LegalAccordion
          docId="cgu"
          checked={acceptedCGU}
          onChange={setAcceptedCGU}
        />
        <p style={{ fontSize: 13, color: 'var(--eb-muted)', margin: '4px 0 12px', lineHeight: 1.5 }}>
          En vous inscrivant, vous reconnaissez également avoir pris connaissance de notre{' '}
          <Link to="/legal/politique-confidentialite" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--eb-brand)', fontWeight: 600 }}>
            Politique de confidentialité
          </Link>.
        </p>

        <button
          type="submit"
          disabled={isSignupLoading || !isPwdValid || !acceptedCGU}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            cursor: (isSignupLoading || !isPwdValid || !acceptedCGU) ? 'not-allowed' : 'pointer',
            opacity: (isSignupLoading || !isPwdValid || !acceptedCGU) ? 0.55 : 1
          }}
        >
          {isSignupLoading ? <span className="eb-spinner eb-spinner--sm eb-spinner--light" aria-hidden="true" /> : null}
          {isSignupLoading ? 'Inscription…' : "S'inscrire"}
        </button>

        {message.text && (
          <div className={`eb-alert ${message.isSuccess ? 'eb-alert--success' : 'eb-alert--danger'}`} role="alert" style={{ marginTop: 16 }}>
            {message.text}
          </div>
        )}

        <div className="eb-auth__footer">
          Déjà un compte ? <Link to={`/logingeneral/${code}`}>Se connecter</Link>
        </div>
      </form>
    </div>
    </div>
  );
};

export default MerchantSignup;
